import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { resolveAsset } from "../lib/assets";

/**
 * Avatar with optional decoration layer composited on top.
 * Supports animated formats (GIF/WebP/APNG) natively — decoration is a
 * separate `<img>` stacked via CSS.
 *
 * Live-syncs from Discord via the `bio:discord-profile` event emitted by
 * the Lanyard widget when the relevant sync flags are on.
 */
export function mountAvatar(config: ProfileConfig["profile"]["avatar"]): HTMLElement {
  const wrap = el("div", { class: "avatar" });
  const base = el("img", {
    class: "avatar__image",
    src: resolveAsset(config.src),
    alt: "",
    draggable: false,
  }) as HTMLImageElement;
  wrap.append(base);

  let deco: HTMLImageElement | null = null;
  if (config.decoration) {
    deco = el("img", {
      class: "avatar__decoration",
      src: resolveAsset(config.decoration),
      alt: "",
      draggable: false,
      "aria-hidden": "true",
    }) as HTMLImageElement;
    wrap.append(deco);
  }

  if (config.syncAvatarFromDiscord || config.syncDecorationFromDiscord) {
    const listener = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { avatarUrl?: string | null; decorationUrl?: string | null }
        | undefined;
      if (!detail) return;

      if (config.syncAvatarFromDiscord && detail.avatarUrl && base.src !== detail.avatarUrl) {
        base.src = detail.avatarUrl;
      }
      if (config.syncDecorationFromDiscord && detail.decorationUrl) {
        if (!deco) {
          deco = el("img", {
            class: "avatar__decoration avatar__decoration--discord",
            src: detail.decorationUrl,
            alt: "",
            draggable: false,
            "aria-hidden": "true",
          }) as HTMLImageElement;
          wrap.append(deco);
        } else if (deco.src !== detail.decorationUrl) {
          deco.src = detail.decorationUrl;
          deco.classList.add("avatar__decoration--discord");
        }
      }
    };
    document.addEventListener("bio:discord-profile", listener);
    wrap.addEventListener("bio:teardown", () => {
      document.removeEventListener("bio:discord-profile", listener);
    });
  }

  return wrap;
}
